import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { queryParams, withQueryParams } from '@shared/commonFunctions';
import { useQueryState } from '@shared/hooks/useQueryState';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { useTabs } from '@shared/hooks/useTabs';
import { ModalWrapper } from '@styles/StyledDiv';

import { DiscStyleList, DiscStyleListItem } from '@styles/CommonStyles';
import { getTabs } from '@store/reducers/tabsSlice';
import { IContact } from '../../../components/Deals/interfaces';
import { ILead } from '../../../components/Leads/types/interfaces/interfaces';
import { requestSwapCoBorrower, SwapBorrowerRequest } from './queries';
// eslint-disable-next-line import/order

interface ISwapAlertProps {
  assetId?: string;
  leadId?: string;
  borrower?: IContact;
  coBorrower?: IContact;
  dealId?: string;
  assetNumericId?: number;
  numericId?: number;
  onClose?: () => void;
  openSwapAlert: boolean;
  setOpenSwapAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SwapConfirmationModal = ({
  assetId,
  leadId,
  borrower,
  coBorrower,
  dealId,
  onClose,
  openSwapAlert,
  assetNumericId,
  numericId,
  setOpenSwapAlert,
  setLoading
}: ISwapAlertProps) => {
  const [contactIdQuery] = useQueryState('id');
  const navigate = useNavigate();
  const tabs = useSelector(getTabs);
  const  params = queryParams();
  const { closeTab, updateCurrentTab } = useTabs();

  const { data: contactStipExist, isLoading, isValidating, error } = useSWR(params?.id && `${SERVICES.DOCUMENT}/stips/request/exist/${params.id}`);

  const { data: leads = [], isLoading: isLeadsLoading } = useSWR<ILead[]>(
    contactIdQuery && `/${SERVICES.LEAD}/contacts/${contactIdQuery}/leads`,
    {
      revalidateOnMount: true,
    }
  );

  const loading = useMemo(() => (!params?.id && !error) || (isLoading || isValidating) || isLeadsLoading, [params?.id, isLoading, isValidating, error]);
  
  const onConfirmSwap = useCallback(async () => {
    setLoading(true);
    setOpenSwapAlert(false);
    const leadIds = leads.filter(({ assetId: assetIdFromLead }) => assetIdFromLead === assetId).map(({ id }) => id);
    const swapRequest = {
      leadIds, 
      assetId,
      coContactId: coBorrower?.id, 
      contactId: borrower?.id, 
      coContactNumericId: coBorrower?.numericId,
      dealId
    } as SwapBorrowerRequest;
    const resp = await requestSwapCoBorrower(swapRequest);
    const route = withQueryParams('/contact-manager', { id: coBorrower?.id });
    const title = coBorrower?.contactName ?? '';

    const tabIdx =
      tabs &&
      tabs.findIndex((tab) => {
        const urlParams = new URLSearchParams(tab.route.split('?')[1]);
        const id = urlParams?.get('id');
        return id === coBorrower?.id;
      });

    if (resp && (resp.status === 200 || resp.status === 204)) {
      onClose && onClose();
      navigate(route, { replace: true });
      if (tabIdx !== -1) {
        closeTab(tabIdx);
      }
      updateCurrentTab({ title });
    }
    setLoading(false);
    setTimeout(function() {
      window.location.reload();
    }, 500);
  }, [borrower, coBorrower, leadId, dealId, tabs]);

  return (
    <Modal
      showFooter
      cancelButtonText="No"
      buttonText="Yes"
      isLoading={loading}
      open={openSwapAlert}
      dialogMinWidth="986px"
      onConfirm={onConfirmSwap}
      onClose={() => setOpenSwapAlert(false)}
    >
      <ModalWrapper>
        <Typography variant="h3" mb={2}>Do you want to save this deal and swap Co-Borrower?</Typography>

        <Typography fontSize={15} marginLeft={1}> The following will happen:</Typography>
        <DiscStyleList>
          {!!assetNumericId && <DiscStyleListItem> {`Asset [ ${assetNumericId} ] will be moved to co-borrower [ ${coBorrower?.contactName} ]`}</DiscStyleListItem>}
          {!!numericId && <DiscStyleListItem> {`Current deal [ ${numericId} ] and deals associated with the asset will be moved to co-borrower [ ${coBorrower?.contactName} ]`}</DiscStyleListItem>}
          <DiscStyleListItem> The current borrower will become the co-borrower</DiscStyleListItem>
          {contactStipExist &&
            <DiscStyleListItem>
              Swapping the borrowers has caused the STIPS session to expire. Request STIPS again to create a new request and link to be sent.
            </DiscStyleListItem>
          }
        </DiscStyleList>
      </ModalWrapper>
    </Modal>
  );
};